import type {
  RdScheduleAppointment,
  RdScheduleCadence,
  RdScheduleFeeStatus,
  RdScheduleTag,
} from "../../../shared/rd-schedule";

// NO-875. The words and groupings the appointment details panel shows, derived
// from one RD Schedule week appointment (NO-868). A leaf module with only type
// imports, so `tsx --test` reads it and the panel stays a thin renderer. Dates
// are read in the week's zone, never the browser's.

export type DetailsGroup = "upcoming" | "past";

/** Upcoming visits take the video and documentation sections; the rest are past. */
export function detailsGroup(appointment: RdScheduleAppointment): DetailsGroup {
  return appointment.calendarStatus === "upcoming" ? "upcoming" : "past";
}

const WEEKDAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

function parts(
  iso: string,
  timezone: string,
  options: Intl.DateTimeFormatOptions
): Record<string, string> {
  const formatted = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    ...options,
  }).formatToParts(new Date(iso));

  return Object.fromEntries(formatted.map((part) => [part.type, part.value]));
}

function clock(iso: string, timezone: string): { time: string; period: string } {
  const p = parts(iso, timezone, { hour: "numeric", minute: "2-digit", hour12: true });

  return { time: `${p.hour}:${p.minute}`, period: (p.dayPeriod ?? "").toUpperCase() };
}

function dayLabel(iso: string, timezone: string): string {
  const p = parts(iso, timezone, { weekday: "short", month: "short", day: "numeric" });

  return `${p.weekday}, ${p.month} ${p.day}`;
}

/** "Fri, Oct 30, 11:00 to 11:45 AM"; the period shows once when both ends share it. */
export function whenLine(
  appointment: Pick<RdScheduleAppointment, "startsAt" | "endsAt">,
  timezone: string
): string {
  const start = clock(appointment.startsAt, timezone);
  const end = clock(appointment.endsAt, timezone);
  const range =
    start.period === end.period
      ? `${start.time} to ${end.time} ${end.period}`
      : `${start.time} ${start.period} to ${end.time} ${end.period}`;

  return `${dayLabel(appointment.startsAt, timezone)}, ${range}`;
}

/** "Mon, Nov 2 at 2:15 PM". */
export function dayAtLine(iso: string, timezone: string): string {
  const at = clock(iso, timezone);

  return `${dayLabel(iso, timezone)} at ${at.time} ${at.period}`;
}

function monthDay(iso: string, timezone: string): string {
  const p = parts(iso, timezone, { month: "short", day: "numeric" });

  return `${p.month} ${p.day}`;
}

function listWithAnd(items: string[]): string {
  if (items.length <= 1) return items.join("");

  if (items.length === 2) return `${items[0]} and ${items[1]}`;

  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

/** "Follow-up, 45 min". */
export function typeLine(appointment: RdScheduleAppointment): string {
  return `${appointment.appointmentType.name}, ${appointment.lengthMinutes} min`;
}

export const TAG_LABELS: Record<RdScheduleTag, string> = {
  initial: "INITIAL",
  s_code: "S CODE",
};

function clockFromLocal(hhmm: string): string {
  const [hourText = "0", minute = "00"] = hhmm.split(":");
  const hour = Number(hourText);
  const period = hour >= 12 ? "PM" : "AM";
  const twelve = hour % 12 === 0 ? 12 : hour % 12;

  return `${twelve}:${minute} ${period}`;
}

/** "Recurring every Friday at 11:00 AM"; null when the visit is not in a series. */
export function recurringLine(appointment: RdScheduleAppointment): string | null {
  const rule = appointment.recurring;
  if (!rule) return null;

  const weekday = WEEKDAY_NAMES[rule.weekday - 1] ?? "week";
  const at = clockFromLocal(rule.startTime);
  const cadence: RdScheduleCadence | null = rule.cadence;

  if (cadence === null) return `Recurring on ${weekday}s at ${at}`;

  if (cadence === "biweekly") return `Recurring every other ${weekday} at ${at}`;

  if (cadence === "every_4_weeks")
    return `Recurring every 4 weeks on ${weekday} at ${at}`;

  return `Recurring every ${weekday} at ${at}`;
}

/**
 * The line under the header: for an upcoming visit, the Client's later visits
 * ("Booked ahead: Nov 6 and Nov 13"); for a past one, the visit that follows
 * ("Next visit: Mon, Nov 2 at 2:15 PM"). Null when there is none to name.
 */
export function aheadLine(
  appointment: RdScheduleAppointment,
  timezone: string
): string | null {
  if (detailsGroup(appointment) === "upcoming") {
    if (appointment.bookedAhead.count === 0) return null;

    // `count` is every booked visit after now, this one included; `starts`
    // lists only the first few. Later visits can be counted only while this
    // visit sits inside that sample; past it, name the next visit instead.
    const starts = appointment.bookedAhead.starts;
    const later = starts.filter((iso) => iso > appointment.startsAt);
    const insideSample =
      starts.length >= appointment.bookedAhead.count ||
      appointment.startsAt < (starts[starts.length - 1] ?? "");
    if (!insideSample) return nextVisitLine(appointment, timezone);

    const laterCount = appointment.bookedAhead.count - (starts.length - later.length);
    if (laterCount <= 0) return null;

    const days = later.map((iso) => monthDay(iso, timezone));
    const more = laterCount - later.length;
    const named = more > 0 ? [...days, `${more} more`] : days;

    return `Booked ahead: ${listWithAnd(named)}`;
  }

  return nextVisitLine(appointment, timezone);
}

function nextVisitLine(
  appointment: RdScheduleAppointment,
  timezone: string
): string | null {
  return appointment.followingVisitAt
    ? `Next visit: ${dayAtLine(appointment.followingVisitAt, timezone)}`
    : null;
}

/** The Client's first word of a name, or the fallback when there is no name. */
export function firstNameOf(name: string | null | undefined, fallback: string): string {
  const trimmed = name?.trim();

  return trimmed ? (trimmed.split(/\s+/)[0] ?? trimmed) : fallback;
}

function firstName(appointment: RdScheduleAppointment): string {
  return firstNameOf(appointment.client.name, "This Client");
}

/** The soft rose note's body. */
export function noNextBody(appointment: RdScheduleAppointment): string {
  return `${firstName(appointment)} has nothing booked after this visit. Book the next visit before this session ends, ideally two ahead.`;
}

export type StatusTone = "success" | "danger" | "warning" | "muted";

export interface StatusLine {
  text: string;
  tone: StatusTone;
}

function timeBefore(minutes: number): string {
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;

  const hours = Math.floor(minutes / 60);

  return `${hours} ${hours === 1 ? "hour" : "hours"}`;
}

/**
 * The status line in the header. Upcoming reads the Client's confirmation, and
 * is null until Healthie's confirmation is stored (NO-881).
 */
export function statusLine(appointment: RdScheduleAppointment): StatusLine | null {
  switch (appointment.calendarStatus) {
    case "upcoming": {
      if (appointment.confirmation === "confirmed") {
        return {
          text: appointment.client.name
            ? `Confirmed by ${firstName(appointment)}`
            : "Confirmed by the Client",
          tone: "success",
        };
      }

      return appointment.confirmation === "unconfirmed"
        ? { text: "Not confirmed yet", tone: "muted" }
        : null;
    }

    case "done":
      return { text: "Done", tone: "success" };
    case "cancelled":
      return { text: "Cancelled", tone: "warning" };
    case "rescheduled":
      return { text: "Rescheduled", tone: "muted" };

    case "late_cancel": {
      const before = appointment.fee?.cancelledMinutesBefore;

      return {
        text:
          typeof before === "number" && before >= 0
            ? `Late cancel, ${timeBefore(before)} before the visit`
            : "Late cancel",
        tone: "danger",
      };
    }

    case "no_show":
      return { text: "No-show", tone: "danger" };
  }
}

export interface FeeCopy {
  title: string;
  body: string;
  /** The one action, the opposite of the rule's default; null on an exempt plan. */
  action: { kind: "charge" | "waive"; label: "Charge the fee anyway" | "Waive" } | null;
}

/** What the Fee section says about the computed rule (NO-868's `fee`). */
export function feeCopy(
  fee: RdScheduleFeeStatus,
  appointment: RdScheduleAppointment
): FeeCopy {
  const who = firstName(appointment);
  const action =
    fee.decision === "waive"
      ? ({ action: { kind: "charge", label: "Charge the fee anyway" } } as const)
      : ({ action: { kind: "waive", label: "Waive" } } as const);

  switch (fee.reason) {
    case "free_late_cancel":
      return {
        title: "Allowed, no fee",
        body: `${who} is within this quarter's free late cancel allowance, so the fee is waived.`,
        ...action,
      };
    case "late_cancel_limit_reached":
      return {
        title: "Not allowed, fee applies",
        body: `${who} has used this quarter's free late cancels.`,
        ...action,
      };
    case "no_show_fee":
      return {
        title: "No-show, fee applies",
        body: "A no-show carries a fee right away.",
        ...action,
      };
    case "plan_type_exempt":
      return {
        title: "No fee",
        body: `${who}'s plan never carries a fee for a ${fee.kind === "no_show" ? "no-show" : "late cancel"}.`,
        action: null,
      };
  }
}

/** "Client since Jun 2026 · 9 visits"; "New client, first visit" before one occurred. */
export function clientSinceLine(
  appointment: RdScheduleAppointment,
  timezone: string
): string {
  const { since, visitCount } = appointment.client;

  if (!since || visitCount === 0) {
    return detailsGroup(appointment) === "past"
      ? "No visits yet"
      : "New client, first visit";
  }

  const p = parts(since, timezone, { month: "short", year: "numeric" });

  return `Client since ${p.month} ${p.year} · ${visitCount} ${visitCount === 1 ? "visit" : "visits"}`;
}

/** The chart note line: Healthie's status text, or a quiet empty state until NO-881. */
export function chartNoteLine(appointment: RdScheduleAppointment): string {
  const status = appointment.chartNote?.trim();

  return status ? status : "Status not available yet";
}

/** The Client's video link when it is a web address; anything else is not offered. */
export function safeJoinUrl(appointment: RdScheduleAppointment): string | null {
  if (!appointment.joinUrl) return null;

  try {
    const url = new URL(appointment.joinUrl);

    return url.protocol === "https:" || url.protocol === "http:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

/** Where Open patient page goes. */
export function patientPagePath(patientId: string): string {
  return `/patients/${encodeURIComponent(patientId)}`;
}
